'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ExternalLink, ShieldCheck, ArrowUp, Cpu, MapPin, MessageCircle } from 'lucide-react';
import { getCountry, getAllCountries } from '@/lib/GeoGraph';
import { getCountryByCode } from '@/lib/countries';
import { COMPANY_ADDRESS } from '@/lib/company-address';
import BuyerMcpModal from './BuyerMcpModal';

interface MarketplaceFooterProps {
  country?: string;
  onCountryClick?: () => void;
  onGarageClick?: () => void;
  onVinClick?: () => void;
  onRequestPartClick?: () => void;
  onAuthClick?: () => void;
}

export default function MarketplaceFooter({
  country: countryProp = 'GH',
  onCountryClick,
  onGarageClick,
  onVinClick,
  onRequestPartClick,
  onAuthClick,
}: MarketplaceFooterProps) {
  const country = getCountry(countryProp);
  const countryMeta = getCountryByCode(country.code) || { flag: '🇬🇭', name: country.name };
  const allCountries = getAllCountries();
  const [isMcpModalOpen, setIsMcpModalOpen] = useState(false);

  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <footer className="w-full bg-[#050B18] text-white pt-16 pb-24 lg:pb-16 mt-20 border-t border-white/10">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pb-12 border-b border-white/10">
          {/* Col 1: Brand + Attribution */}
          <div className="md:col-span-4 space-y-4">
            <Link href="/" className="inline-block" aria-label="Parts4Drive Home">
              <div
                className="w-[180px] h-[45px] sm:w-[220px] sm:h-[55px] flex items-center justify-start shrink-0 overflow-hidden"
                data-slot="logo-holder"
              >
                <img
                  src="/logo/logowhite.svg"
                  alt="Parts4Drive"
                  className="w-full h-full object-contain object-left"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </Link>
            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed max-w-sm">
              Autonomous automotive commerce platform connecting vehicle owners, mechanics, and enterprise fleets with verified spare parts shops across Africa, backed by guaranteed exact fitment.
            </p>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] text-slate-300">
              <span className="size-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>All Systems Operational • {country.name} Gateway Active ({country.currency})</span>
            </div>
            <p className="text-[11px] text-slate-400">
              Parts4Drive is an automotive commerce platform operated by <strong className="text-white font-semibold">NGiSense Technologies, Inc.</strong>
            </p>
            <div className="pt-2 text-[11px] text-slate-400 space-y-1">
              <div className="flex items-start gap-1.5">
                <MapPin className="size-3.5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Headquarters:</span>{' '}
                  <span>{COMPANY_ADDRESS.fullAddress}</span>
                  <div className="flex items-center gap-2 mt-1">
                    <a
                      href={COMPANY_ADDRESS.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 hover:underline font-medium"
                    >
                      <span>Google Maps Listing</span>
                      <ExternalLink className="size-2.5" />
                    </a>
                    <span>•</span>
                    <a
                      href={COMPANY_ADDRESS.googleDirectionsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-300 hover:text-white inline-flex items-center gap-1 hover:underline"
                    >
                      <span>Get Directions</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Col 2: Popular Vehicle Makes */}
          <div className="md:col-span-2 space-y-3">
            <h4 className="text-white font-bold text-xs uppercase tracking-wider font-mono">
              Vehicle Catalog
            </h4>
            <ul className="space-y-2 text-xs text-slate-300">
              <li>
                <Link href={`/${country.slug}/toyota`} className="hover:text-white transition-colors">
                  Toyota Spare Parts
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/honda`} className="hover:text-white transition-colors">
                  Honda Spare Parts
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/mercedes-benz`} className="hover:text-white transition-colors">
                  Mercedes-Benz Parts
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/lexus`} className="hover:text-white transition-colors">
                  Lexus Spare Parts
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/nissan`} className="hover:text-white transition-colors">
                  Nissan Spare Parts
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/hyundai`} className="hover:text-white transition-colors">
                  Hyundai Spare Parts
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/kia`} className="hover:text-white transition-colors">
                  Kia Spare Parts
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/shop`} className="hover:text-emerald-400 font-semibold transition-colors">
                  View All Makes &rarr;
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Part Categories */}
          <div className="md:col-span-2 space-y-3">
            <h4 className="text-white font-bold text-xs uppercase tracking-wider font-mono">
              Part Categories
            </h4>
            <ul className="space-y-2 text-xs text-slate-300">
              <li>
                <Link href={`/${country.slug}/category/braking`} className="hover:text-white transition-colors">
                  Braking Systems &amp; Pads
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/category/suspension`} className="hover:text-white transition-colors">
                  Suspension &amp; Shocks
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/category/engine`} className="hover:text-white transition-colors">
                  Engine &amp; Filtration
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/category/body`} className="hover:text-white transition-colors">
                  Body Panels &amp; Bumpers
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/category/lighting`} className="hover:text-white transition-colors">
                  LED Headlights &amp; Lamps
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/category/transmission`} className="hover:text-white transition-colors">
                  Transmission &amp; Axles
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/category/electrical`} className="hover:text-white transition-colors">
                  Alternators &amp; Starters
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/shop`} className="hover:text-emerald-400 font-semibold transition-colors">
                  View All Categories &rarr;
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Commercial Depots & Hubs */}
          <div className="md:col-span-2 space-y-3">
            <h4 className="text-white font-bold text-xs uppercase tracking-wider font-mono">
              Commercial Hubs
            </h4>
            <ul className="space-y-2 text-xs text-slate-300">
              <li>
                <Link href={`/${country.slug}/hubs/${country.slug === 'gh' ? 'abossey-okai' : 'ladipo'}`} className="hover:text-white transition-colors">
                  {country.slug === 'gh' ? 'Abossey Okai Central' : 'Ladipo Auto Market'}
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/hubs/${country.slug === 'gh' ? 'kumasi-magazine' : 'aspamda'}`} className="hover:text-white transition-colors">
                  {country.slug === 'gh' ? 'Kumasi Magazine Depot' : 'Trade Fair / ASPAMDA'}
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/hubs`} className="hover:text-white transition-colors">
                  Commercial Hubs 360&deg;
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/sellers`} className="hover:text-white transition-colors">
                  Verified Depot Merchants
                </Link>
              </li>
              <li>
                <Link href="/ng/sell" className="hover:text-white transition-colors inline-flex items-center gap-1 text-emerald-400 font-semibold">
                  Sell on Parts4Drive (Nigeria)
                </Link>
              </li>
              <li>
                <Link
                  href="/dashboard"
                  className="hover:text-white transition-colors inline-flex items-center gap-1 text-slate-300 font-medium"
                >
                  Merchant Dashboard
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 5: Buyer Protection & Support */}
          <div className="md:col-span-2 space-y-3">
            <h4 className="text-white font-bold text-xs uppercase tracking-wider font-mono">
              Buyer Protection
            </h4>
            <ul className="space-y-2 text-xs text-slate-300">
              <li>
                <Link href="/how-it-works" className="hover:text-white transition-colors">
                  How It Works
                </Link>
              </li>
              <li>
                <Link href={`/${country.slug}/shop`} className="text-emerald-400 hover:text-emerald-300 font-semibold transition-colors">
                  100% Fitment Guarantee
                </Link>
              </li>
              <li>
                {onRequestPartClick ? (
                  <button onClick={onRequestPartClick} className="hover:text-white transition-colors cursor-pointer text-left">
                    Request a Part
                  </button>
                ) : (
                  <Link href="/contact" className="hover:text-white transition-colors">
                    Request a Part
                  </Link>
                )}
              </li>
              <li>
                <Link href="/faq" className="hover:text-white transition-colors">
                  Frequently Asked Questions
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors">
                  Engineering &amp; Auto Blog
                </Link>
              </li>
              <li>
                <Link href="/contact" className="hover:text-white transition-colors">
                  Contact Support
                </Link>
              </li>
              <li>
                <a
                  href={COMPANY_ADDRESS.whatsappChannelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
                >
                  <MessageCircle className="size-3.5 text-emerald-400" />
                  <span>WhatsApp Channel</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    OFFICIAL
                  </span>
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setIsMcpModalOpen(true)}
                  className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 font-semibold transition-colors cursor-pointer text-left"
                >
                  <Cpu className="size-3.5" />
                  <span>Connect AI / MCP Agent</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Pan-African Network Quick Links */}
        <div className="py-6 border-b border-white/5 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-[11px] uppercase tracking-wider font-mono">Active Markets:</span>
            <div className="flex flex-wrap items-center gap-3">
              {allCountries.slice(0, 8).map((c) => (
                <Link
                  key={c.slug}
                  href={`/${c.slug}`}
                  className={`hover:text-white transition-colors ${
                    c.slug === country.slug ? 'text-emerald-400 font-bold' : 'text-slate-400'
                  }`}
                >
                  {c.name} ({c.currency})
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Actions */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <p>
              &copy; {new Date().getFullYear()} Parts4Drive. All rights reserved. Operated by <strong className="text-slate-300">NGiSense Technologies, Inc.</strong>
            </p>
            <div className="flex items-center gap-3 text-[11px] text-slate-400">
              <Link href="/privacy" rel="nofollow" className="hover:text-white transition-colors">Privacy</Link>
              <span>•</span>
              <Link href="/terms" rel="nofollow" className="hover:text-white transition-colors">Terms</Link>
              <span>•</span>
              <Link href="/compliance" rel="nofollow" className="hover:text-white transition-colors">Compliance</Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {onCountryClick ? (
              <button
                type="button"
                onClick={onCountryClick}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white transition-colors cursor-pointer"
              >
                <span>{countryMeta.flag}</span>
                <span>{countryMeta.name}</span>
                <span className="text-[10px] text-slate-400">Change</span>
              </button>
            ) : (
              <Link
                href={`/${country.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white transition-colors"
              >
                <span>{countryMeta.flag}</span>
                <span>{countryMeta.name}</span>
              </Link>
            )}
            <button
              type="button"
              onClick={scrollToTop}
              className="hover:text-white transition-colors flex items-center gap-1 cursor-pointer"
            >
              <span>Back to top</span>
              <ArrowUp className="size-3" />
            </button>
          </div>
        </div>
      </div>

      <BuyerMcpModal
        isOpen={isMcpModalOpen}
        onClose={() => setIsMcpModalOpen(false)}
      />
    </footer>
  );
}
